import { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useIntl } from 'react-intl';
import http from 'src/services/httpService';
import { capitalizeFirstLetter } from 'src/utils/string';

import useAuth from 'src/hooks/useAuth';
import Input from 'src/componentes/input';
import CargoLookup from 'src/lookups/cargo';
import AreaLookup from 'src/lookups/item-organizacao';
import TagLookup from 'src/lookups/TagLookup';
import { ADDONS } from 'src/utils/constants';
import PanelCollapse from 'src/componentes/panel-collapse';
import PeriodoData from 'src/paginas/data/periodo-data';
import Checkbox from 'src/componentes/checkbox';
import ColaboradorSimplificadoLookup from 'src/lookups/ColaboradorSimplificadoLookup';
import TipoVinculoFuncaoLookup from 'src/lookups/tipo-vinculo-funcao';
import errorHandler from 'src/utils/error-handler';

const FuncaoSearch = ({ model }) => {
  const intl = useIntl();
  const { terms: resources, addons } = useAuth();
  const [idsVinculosIgnorados, setIdsVinculosIgnorados] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const possuiAddonNegociacaoMetas =
    addons?.find((x) => x.id === ADDONS.NEGOCIACAO_METAS && x.ativo) != null;

  let showArea =
    model.getModel('vinculo').value?.id === 1 ||
    model.getModel('vinculo').value?.id === 3;
  let showColaborador = model.getModel('vinculo').value?.id === 2;
  let showTipoFiltroData =
    model.getModel('vinculo').value?.id === 1 ||
    model.getModel('vinculo').value?.id === 3 ||
    model.getModel('vinculo').value?.id === 2;

  const handleChangeFiltro = (field, value) => {
    model.getModel(field).requestChange(value);
  };

  const handleChangeVinculo = (value) => {
    model.getModel('vinculo').requestChange(value);

    model.getModel('areaVinculo').requestChange(null);
    model.getModel('areasSubordinadas').requestChange(false);
    model.getModel('colaborador').requestChange(null);
    model.getModel('tipoFiltroData').requestChange(null);
    model.getModel('dataInicio').requestChange(null);
    model.getModel('dataTermino').requestChange(null);
  };

  const getConfiguration = async () => {
    try {
      const response = await http.post(
        `/ConfiguracaoCardMeta/ObterConfiguracaoVincularFuncaoOrganizacao`
      );

      setIdsVinculosIgnorados(response.data === false ? [1] : []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    getConfiguration();
  }, []);

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <PanelCollapse
        header={capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.geral' })
        )}
        open={true}
        ehAccordionDrawer={true}
      >
        <Row>
          <Col md={12}>
            <Input
              model={{
                label: intl.formatMessage({ id: 'idIntegracao' }),
                value: model.getModel('idIntegracao').value,
                requestChange: model.getModel('idIntegracao').requestChange,
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <CargoLookup
              model={{
                label: intl.formatMessage(
                  { id: 'cargo' },
                  { cargo: resources.cargo }
                ),
                value: model.getModel('cargo').value,
                requestChange: model.getModel('cargo').requestChange,
              }}
            ></CargoLookup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TagLookup
              multi
              model={{
                label: capitalizeFirstLetter(
                  intl.formatMessage({ id: 'tags' }, { tags: resources.tags })
                ),
                value: model.getModel('tags').value,
                requestChange: model.getModel('tags').requestChange,
              }}
              data={{ funcionalidadesIdSelecionaveis: [10] }}
              podeEditar={false}
              showConditional={true}
              modelConditional={{
                label: intl.formatMessage({ id: 'condicional' }),
                value: model.getModel('tipoCondicional').value ?? {
                  id: 2,
                  descricao: intl.formatMessage({ id: 'contem' }),
                },
                requestChange: model.getModel('tipoCondicional').requestChange,
              }}
            />
          </Col>
        </Row>
      </PanelCollapse>
      <PanelCollapse
        header={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'vinculoFuncao' },
            { funcao: resources.funcao }
          )
        )}
        open={true}
        ehAccordionDrawer={true}
      >
        <Row>
          <Col md={12}>
            {!loadingConfig && (
              <TipoVinculoFuncaoLookup
                clearable={true}
                data={{ idsIgnorados: idsVinculosIgnorados }}
                model={{
                  label: capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: 'vinculoFuncao' },
                      { funcao: resources.funcao }
                    )
                  ),
                  value: model.getModel('vinculo').value,
                  requestChange: (value) => handleChangeVinculo(value),
                }}
              />
            )}
          </Col>
          {showArea && (
            <>
              <Col md={12}>
                <AreaLookup
                  required
                  model={{
                    label: capitalizeFirstLetter(
                      intl.formatMessage(
                        { id: 'area' },
                        { area: resources.area }
                      )
                    ),
                    value: model.getModel('areaVinculo').value,
                    requestChange: model.getModel('areaVinculo').requestChange,
                    errors: model.getModel('areaVinculo').errors,
                  }}
                ></AreaLookup>
              </Col>

              <Col md={12}>
                <Checkbox
                  style={{ marginTop: 10, marginBottom: 15 }}
                  model={{
                    label: capitalizeFirstLetter(
                      intl.formatMessage(
                        { id: 'label.considerarAreasSubordinadas' },
                        { areas: resources.areas }
                      )
                    ),
                    value: model.getModel('areasSubordinadas').value,
                    requestChange:
                      model.getModel('areasSubordinadas').requestChange,
                  }}
                />
              </Col>
            </>
          )}
          {showColaborador && (
            <Col md={12}>
              <ColaboradorSimplificadoLookup
                required
                clearable
                multi={false}
                model={{
                  label: capitalizeFirstLetter(resources.colaborador),
                  value: model.getModel('colaborador').value,
                  requestChange: model.getModel('colaborador').requestChange,
                  errors: model.getModel('colaborador').errors,
                }}
              />
            </Col>
          )}
          {showTipoFiltroData && (
            <Col>
              <PeriodoData
                clearable={true}
                data={{ idsIgnorados: [7, 8, 9] }}
                tipoFiltroData={model.getModel('tipoFiltroData').value}
                dataInicio={model.getModel('dataInicio').value}
                dataTermino={model.getModel('dataTermino').value}
                onChange={handleChangeFiltro}
                adjustSize={true}
                eachPerLine={true}
              />
            </Col>
          )}
        </Row>
      </PanelCollapse>
      {possuiAddonNegociacaoMetas && (
        <PanelCollapse
          header={capitalizeFirstLetter(
            intl.formatMessage({ id: 'negociacaoMetas' })
          )}
          open={true}
          ehAccordionDrawer={true}
        >
          <Row>
            <Col md={12}>
              <AreaLookup
                model={{
                  label: capitalizeFirstLetter(
                    intl.formatMessage({ id: 'area' }, { area: resources.area })
                  ),
                  value: model.getModel('area').value,
                  requestChange: model.getModel('area').requestChange,
                }}
              ></AreaLookup>
            </Col>
          </Row>
        </PanelCollapse>
      )}
    </div>
  );
};

export default FuncaoSearch;
